import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CheckIcon from './components/CheckIcon';
import { useAuth } from './contexts/AuthContext';
import { usePlan } from './contexts/PlanContext';
import axios from 'axios';
import './styles/PricingPage.css';

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const { user } = useAuth();
  const { refreshPlanData } = usePlan();
  const navigate = useNavigate();
  
  // Use relative URL for same-domain deployments (like Vercel)
// Use full URL only for cross-domain deployments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});
  
  const handleToggle = () => {
    setAnnual(!annual);
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
  };

  // Currency symbols
  const currencySymbols = {
    USD: 'USD',
    EUR: 'EUR',
    INR: 'INR'
  };

  // Pricing data
  const pricingData = {
    monthly: {
      USD: { Free: 0, Individual: 20, Team: 150 },
      EUR: { Free: 0, Individual: 17, Team: 127 },
      INR: { Free: 0, Individual: 1720, Team: 12750 }
    },
    annual: {
      USD: { Free: 0, Individual: 192, Team: 1440 },
      EUR: { Free: 0, Individual: 163, Team: 1224 },
      INR: { Free: 0, Individual: 16510, Team: 122400 }
    }
  };

  const getPrice = (planName) => {
    const period = annual ? 'annual' : 'monthly';
    return pricingData[period][currency][planName];
  };

  // Team plan slab pricing data
  const teamPricingSlabs = {
    monthly: {
      USD: [
        { maxUsers: 5, price: 150, description: 'Up to 5 users' },
        { maxUsers: 10, price: 300, description: 'Up to 10 users' },
        { maxUsers: 15, price: 450, description: 'Up to 15 users' },
        { maxUsers: null, price: null, description: 'More than 15 users - Contact Sales' }
      ],
      EUR: [
        { maxUsers: 5, price: 127, description: 'Up to 5 users' },
        { maxUsers: 10, price: 254, description: 'Up to 10 users' },
        { maxUsers: 15, price: 381, description: 'Up to 15 users' },
        { maxUsers: null, price: null, description: 'More than 15 users - Contact Sales' }
      ],
      INR: [
        { maxUsers: 5, price: 12750, description: 'Up to 5 users' },
        { maxUsers: 10, price: 25500, description: 'Up to 10 users' },
        { maxUsers: 15, price: 38250, description: 'Up to 15 users' },
        { maxUsers: null, price: null, description: 'More than 15 users - Contact Sales' }
      ]
    },
    annual: {
      USD: [
        { maxUsers: 5, price: 1440, description: 'Up to 5 users' },
        { maxUsers: 10, price: 2880, description: 'Up to 10 users' },
        { maxUsers: 15, price: 4320, description: 'Up to 15 users' },
        { maxUsers: null, price: null, description: 'More than 15 users - Contact Sales' }
      ],
      EUR: [
        { maxUsers: 5, price: 1224, description: 'Up to 5 users' },
        { maxUsers: 10, price: 2448, description: 'Up to 10 users' },
        { maxUsers: 15, price: 3672, description: 'Up to 15 users' },
        { maxUsers: null, price: null, description: 'More than 15 users - Contact Sales' }
      ],
      INR: [
        { maxUsers: 5, price: 122400, description: 'Up to 5 users' },
        { maxUsers: 10, price: 244800, description: 'Up to 10 users' },
        { maxUsers: 15, price: 367200, description: 'Up to 15 users' },
        { maxUsers: null, price: null, description: 'More than 15 users - Contact Sales' }
      ]
    }
  };

  const getTeamPricing = () => {
    const period = annual ? 'annual' : 'monthly';
    return teamPricingSlabs[period][currency];
  };

  const handleContactSales = () => {
    window.open('/contact', '_blank');
  };

  // Add currency notice function
  const getCurrencyNotice = () => {
    if (currency !== 'INR') {
      return (
        <div style={{ color: 'orange', margin: '16px 0', fontWeight: 500 }}>
          Note: Payment will be processed in Indian Rupees (INR). Your bank may charge currency conversion fees. The INR amount will be shown on the payment page.
        </div>
      );
    }
    return null;
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const toastTimeout = useRef(null);

  const handleShowToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(() => {
      setToast({ open: false, message: '', severity });
    }, 5000);
  };

  const handlePlanUpgrade = async (planName, slab = null) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const planType = annual ? 'Annual' : 'Monthly';

      // Individual plan: Razorpay subscription flow (redirect to shortUrl)
      if (planName === 'Individual') {
        const response = await api.post('/payments/create-subscription');
        const { shortUrl } = response.data.data;
        if (shortUrl) {
          window.location.href = shortUrl;
          return;
        }
      }

      // Team plan (and fallback): one-time order + Razorpay Checkout
      const response = await api.post('/payments/create-order', {
        planName,
        planType,
        currency,
        slab
      });

      const responseData = response.data.data;
      const orderId = responseData.orderId;
      const amount = responseData.amount;
      const responseCurrency = responseData.currency;
      const key = responseData.key;

      const options = {
        key: key,
        amount: amount,
        currency: responseCurrency,
        name: 'Velox AI',
        description: `${planName} Plan - ${planType}${slab ? ` - ${slab.description}` : ''}`,
        order_id: orderId,
        handler: async function (response) {
          try {
            const verifyResponse = await api.post('/payments/verify-payment', {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              planName,
              planType,
              slab
            });

            if (verifyResponse.data.status === 'success') {
              await refreshPlanData();
              handleShowToast('Payment successful! Your plan has been upgraded.', 'success');
              navigate('/settings');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            handleShowToast('Payment verification failed. Please contact support.', 'error');
          }
        },
        theme: {
          color: '#3b9efc'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error creating payment:', error);
      console.error('Error response:', error.response?.data);
      handleShowToast(`Failed to create payment: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #0a0a0a 0%, #181c4b 100%)',
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container">
          <h1>Simple, Transparent Pricing</h1>
          <p>Choose the plan that fits your team's needs</p>
          {/* Currency Notice */}
          {getCurrencyNotice()}
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={!annual ? 'active' : ''}>Monthly</span>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={annual} 
                onChange={handleToggle}
              />
              <span className="slider round"></span>
            </label>
            <span className={annual ? 'active' : ''}>Annual <span className="save-tag">Save 20%</span></span>
          </div>

          {/* Currency Toggle */}
          <div className="currency-toggle">
            <span>Currency:</span>
            <div className="currency-buttons">
              {Object.keys(currencySymbols).map((curr) => (
                <button
                  key={curr}
                  className={`currency-btn ${currency === curr ? 'active' : ''}`}
                  onClick={() => handleCurrencyChange(curr)}
                >
                  {curr}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pricing-plans">
        <div className="container">
          <div className="pricing-grid">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h2>Free</h2>
                <div className="price">
                  <span className="amount">{currencySymbols[currency]} {getPrice('Free')}</span>
                  <span className="period">{annual ? ' per user/annum' : ' per user/month'}</span>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b' }}>For individual-level features with limited access</p>
              </div>
              
              <div className="pricing-features">
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px'}}>5 document creation per month across categories</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>10 new chats (5 queries per chat)</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>5 document uploads in the knowledge hub</span>
                </div>
              </div>
              
              <div className="pricing-cta">
                <Link to="/signup" className="btn btn-secondary btn-full">Get Started Free</Link>
              </div>
            </div>

            {/* Individual Plan */}
            <div className="pricing-card highlighted">
              <div className="pricing-popular-tag">Most Popular</div>
              <div className="pricing-header">
                <h2>Individual</h2>
                <div className="price">
                  <span className="amount">{currencySymbols[currency]} {getPrice('Individual')}</span>
                  <span className="period">{annual ? ' per user/annum' : ' per user/month'}</span>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b' }}>For individual-level features with unlimited access</p>
              </div>
              
              <div className="pricing-features">
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document creation across categories</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited chats with unlimited queries per chat</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document uploads in the knowledge hub</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Integration with Slack</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Pre-defined model</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Custom account list addition</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Basic chat support</span>
                </div>
              </div>
              
              <div className="pricing-cta">
                <button 
                  className="btn btn-primary btn-full" 
                  onClick={() => handlePlanUpgrade('Individual')}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Start Individual Plan'}
                </button>
              </div>
            </div>

            {/* Team Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h2>Team</h2>
                <div className="price">
                  <span className="period">Contact Sales</span>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b' }}>For team-level features with flat-rate pricing</p>
              </div>
              
              <div className="pricing-features">
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document creation across categories</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited chats with unlimited queries per chat</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document uploads in the knowledge hub</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Integration with Slack</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Integration with HubSpot</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Option to select AI models</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Centralized Billing</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Shared Documents</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Premium chat and call support</span>
                </div>
              </div>
              
              <div className="pricing-cta">
              <Link to="/contact" className="btn btn-secondary btn-full">Contact Sales</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is the Free plan really free?</h3>
              <p>Yes! Our Free plan is completely free with no hidden costs. You get 5 document creations per month, 10 chats with 5 queries each, and 5 document uploads to get started with Velox AI.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Yes, you can upgrade or downgrade your plan at any time. If you upgrade, the changes take effect immediately. If you downgrade, the changes take effect at the end of your billing cycle.</p>
            </div>
            
            <div className="faq-item">
              <h3>How does Team plan billing work?</h3>
              <p>Team plans use flat-rate pricing starting at $150/month for small teams. Pricing scales with team size and includes all team features. For larger teams or custom pricing, please contact our sales team.</p>
            </div>
            
            <div className="faq-item">
              <h3>What integrations are available?</h3>
              <p>Individual plans include Slack integration. Team plans include both Slack and HubSpot integrations for comprehensive workflow automation.</p>
            </div>
            
            <div className="faq-item">
              <h3>Is there a minimum contract length?</h3>
              <p>Monthly plans have no minimum contract. Annual plans are billed for 12 months and automatically renew at the end of the term.</p>
            </div>
            
            <div className="faq-item">
              <h3>What kind of support do you offer?</h3>
              <p>Free plan includes community support. Individual plans include basic chat support. Team plans include premium chat and call support for dedicated assistance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-background">
          <div className="cta-gradient"></div>
          <div className="cta-particles"></div>
        </div>
        
        <div className="container">
          <h2>Ready to Bring Ambient Intelligence to Your Sales?</h2>
          <p>Start with our Free Plan today — no credit card required</p>
          
          <div className="cta-buttons">
            <Link to="/signup" className="btn btn-primary btn-large">Get Started Free</Link>
            <Link to="/contact" className="btn btn-secondary">Have Questions? Contact Us</Link>
          </div>
        </div>
      </section>
      
      {/* Toast Notification */}
      {toast.open && (
        <div style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 30,
          margin: '0 auto',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{
            background: toast.severity === 'success' ? '#43a047' : '#d32f2f',
            color: '#fff',
            padding: '16px 32px',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            fontWeight: 500,
            fontSize: 16,
            pointerEvents: 'all',
            minWidth: 280,
            textAlign: 'center'
          }}>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
} 